import { useEffect, useMemo, useState } from 'react';
import { Link as LinkIcon, Unlink, Loader2, AlertTriangle } from 'lucide-react';
import irrigationNetworkService, {
  type IrrigationNode,
  type IrrigationLiveNetwork,
  type SensorChannelOption,
} from '../../../services/irrigationNetwork';
import type { EspNowRouter, RelayZone } from '../../../services/espnowIrrigation';
import {
  groupByRouter,
  slotClaim,
  slotKey,
  unlinkedProgramSlots,
  type RelaySlot,
} from '../../../utils/relayCatalogue';
import { nodeStateLabel } from '../../../utils/nodeState';
import RelayActivity from './RelayActivity';
import ZoneLinkSection from './ZoneLinkSection';
import { errorMessage } from '../../../utils/apiResponse';

interface Props {
  node: IrrigationNode;
  network: IrrigationLiveNetwork | null;
  catalogue: RelaySlot[];
  routersByController: Record<string, EspNowRouter[]>;
  catalogueLoading: boolean;
  catalogueError: string | null;
  editable: boolean;
  /** The node as the server returned it after a link change. */
  onLinked: (updated: IrrigationNode) => void | Promise<void>;
  /** The relay whose area the map is currently drawing, if any. */
  drawingZoneFor: string | null;
  onDrawZone: (slot: RelaySlot) => void;
  onCancelDrawZone: () => void;
  /** Null when the zone was deleted. */
  onZoneSaved: (zone: RelayZone | null, routerMac: string, relayIndex: number) => void;
}

/**
 * Point a network node at the hardware that drives or measures it.
 *
 * The link is not decoration: `scheduleAnalysisService` matches a program
 * step's relays to valve nodes by `(router_mac, relay_index)`, and a step whose
 * relay matches nothing solves as though that valve were shut — reported as an
 * "unmapped relay" with no way to act on it until now. Likewise the live
 * divergence check only compares a node's predicted pressure against a reading
 * when the node carries a `sensor_channel_id`.
 *
 * Two rules the UI has to hold up:
 *
 * - **A relay belongs to one node.** There is a unique index behind that, so an
 *   already-claimed relay is offered as unselectable and named, rather than
 *   left to fail on save.
 * - **Only a valve is read as a valve.** A relay on a junction or an outlet is
 *   invisible to the schedule analysis, so the picker is offered on valves and
 *   an existing link elsewhere is shown with the reason it does nothing.
 * - **A pump is the exception.** Programs switch the pump relay alongside the
 *   valves, so a source node takes a relay too. The schedule check lists it as
 *   the step's pump rather than as an unmapped relay; the solver still models a
 *   source as available whenever a valve is open, so the link changes no figure.
 */
export default function DeviceLinkPanel({
  node, network, catalogue, routersByController,
  catalogueLoading, catalogueError, editable, onLinked,
  drawingZoneFor, onDrawZone, onCancelDrawZone, onZoneSaved,
}: Props) {
  const [busy, setBusy] = useState<'relay' | 'sensor' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [relayChoice, setRelayChoice] = useState('');
  const [sensorChoice, setSensorChoice] = useState('');
  const [channels, setChannels] = useState<SensorChannelOption[]>([]);
  const [channelsFailed, setChannelsFailed] = useState(false);

  useEffect(() => { setRelayChoice(''); setSensorChoice(''); setError(null); }, [node.id]);

  useEffect(() => {
    let cancelled = false;
    irrigationNetworkService.listSensorChannels(node.farm_id)
      .then((c) => { if (!cancelled) { setChannels(c); setChannelsFailed(false); } })
      .catch(() => { if (!cancelled) { setChannels([]); setChannelsFailed(true); } });
    return () => { cancelled = true; };
  }, [node.farm_id]);

  const groups = useMemo(
    () => groupByRouter(catalogue, routersByController),
    [catalogue, routersByController],
  );
  const orphans = useMemo(() => unlinkedProgramSlots(catalogue), [catalogue]);

  const linkedSlot = useMemo(() => {
    if (!node.relay_router_mac || node.relay_index === null) return null;
    const want = `${node.relay_router_mac.toUpperCase()}:${node.relay_index}`;
    return catalogue.find(s => slotKey(s) === want) ?? null;
  }, [catalogue, node.relay_router_mac, node.relay_index]);

  const liveRelay = node.relay_router_mac && node.relay_index !== null
    ? network?.relay_state[`${node.relay_router_mac}:${node.relay_index}`] ?? null
    : null;

  const linkedChannel = node.sensor_channel_id
    ? channels.find(c => c.id === node.sensor_channel_id) ?? null
    : null;
  const reading = node.sensor_channel_id
    ? network?.sensor_readings[node.sensor_channel_id] ?? null
    : null;

  /** Channels another node on this farm already measures with. */
  const channelClaims = useMemo(() => {
    const m = new Map<number, string>();
    for (const n of network?.nodes ?? []) {
      if (n.sensor_channel_id && n.id !== node.id) {
        m.set(n.sensor_channel_id, n.name || `${n.node_type} #${n.id}`);
      }
    }
    return m;
  }, [network, node.id]);

  const takesRelay = node.node_type === 'valve' || node.node_type === 'source';
  const canPickRelay = editable && takesRelay;

  const run = async (kind: 'relay' | 'sensor', fn: () => Promise<IrrigationNode>) => {
    setBusy(kind);
    setError(null);
    try {
      await onLinked(await fn());
      // The pick has been made; leaving it selected leaves a live Link button
      // over a relay that is already linked.
      if (kind === 'relay') setRelayChoice(''); else setSensorChoice('');
    } catch (e) {
      setError(errorMessage(e, kind === 'relay' ? 'Relay link failed' : 'Sensor link failed'));
    } finally { setBusy(null); }
  };

  const linkRelay = () => {
    const slot = catalogue.find(s => slotKey(s) === relayChoice);
    if (!slot) return;
    // The router's own spelling of the MAC, not the operator's: the schedule's
    // step relays carry that spelling too, and the analysis matches on the
    // string. A link written in the other case maps to nothing.
    run('relay', () => irrigationNetworkService.linkRelay(node.id, slot.router_mac, slot.relay_index));
  };

  const linkSensor = () => {
    const id = Number(sensorChoice);
    if (!Number.isFinite(id) || id <= 0) return;
    run('sensor', () => irrigationNetworkService.linkSensor(node.id, id));
  };

  return (
    <div className="mt-2 border rounded bg-gray-50 text-xs">
      <div className="px-2 py-1.5 border-b font-semibold flex items-center gap-1">
        <LinkIcon className="w-3 h-3" /> Device link
      </div>

      <div className="p-2 space-y-3">
        {/* ── Relay ───────────────────────────────────────────────────── */}
        <section>
          <div className="font-medium text-gray-700 mb-1">Relay</div>

          {node.relay_router_mac && node.relay_index !== null ? (
            <div className="bg-white border rounded p-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium break-words">
                    {linkedSlot
                      ? `${linkedSlot.label} on ${linkedSlot.router_name || linkedSlot.router_mac.slice(-8)}`
                      : `Relay ${node.relay_index}`}
                  </div>
                  <div className="text-gray-500 break-all">
                    {node.relay_router_mac}:{node.relay_index}
                  </div>
                  {linkedSlot && linkedSlot.programs.length > 0 && (
                    <div className="text-gray-600 mt-0.5">
                      Watered by {linkedSlot.programs.join(', ')}
                    </div>
                  )}
                  {!linkedSlot && !catalogueLoading && !catalogueError && (
                    // Said out loud rather than shown as a bare address: a link
                    // to a relay no controller on this farm lists is a valve the
                    // schedule analysis will never match.
                    <div className="text-amber-700 mt-0.5 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      No node on this farm reports that relay — the hardware may
                      have been replaced or moved to another farm.
                    </div>
                  )}
                </div>
                {editable && (
                  <button
                    className="text-red-600 whitespace-nowrap disabled:opacity-50"
                    disabled={busy !== null}
                    onClick={() => run('relay', () => irrigationNetworkService.unlinkRelay(node.id))}
                  >
                    <Unlink className="w-3 h-3 inline" /> unlink
                  </button>
                )}
              </div>
              {liveRelay && (
                <div className="mt-1 text-gray-600">
                  Currently {liveRelay.on ? 'ON' : 'OFF'}
                  {!liveRelay.online && ' · node not answering'}
                </div>
              )}
              {/* What was asked of this valve, as distinct from what it is
                  doing now. "Currently OFF" above is the same sentence whether
                  a run was never sent, sent and refused, or sent and still
                  sitting in a queue the site has not collected — and those
                  three want three different things done about them. Needs the
                  catalogue's controller id, so it waits for the slot rather
                  than guessing one from the MAC. */}
              {linkedSlot && (
                <RelayActivity
                  controllerId={linkedSlot.controller_id}
                  routerMac={linkedSlot.router_mac}
                  relayIndex={linkedSlot.relay_index}
                  routers={routersByController[linkedSlot.controller_id] ?? []}
                />
              )}
              {/* The area this valve waters. Reached through the relay rather
                  than stored on the node — see ZoneLinkSection. */}
              {linkedSlot && (
                <ZoneLinkSection
                  slot={linkedSlot}
                  editable={editable}
                  drawing={drawingZoneFor === slotKey(linkedSlot)}
                  onDraw={() => onDrawZone(linkedSlot)}
                  onCancelDraw={onCancelDrawZone}
                  onZoneSaved={onZoneSaved}
                />
              )}
            </div>
          ) : (
            <div className="text-gray-500">No relay linked</div>
          )}

          {node.relay_router_mac && !takesRelay && (
            <div className="mt-1 text-amber-700">
              Only valve and pump nodes are read by the schedule check — this
              {' '}{node.node_type} is linked but will not appear in one.
            </div>
          )}

          {canPickRelay && (
            <div className="mt-1.5 space-y-1">
              {catalogueLoading && (
                <div className="text-gray-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading relays…
                </div>
              )}
              {catalogueError && <div className="text-amber-700">{catalogueError}</div>}
              {!catalogueLoading && !catalogueError && groups.length === 0 && (
                <div className="text-gray-500">
                  No controller on this farm reports any relays.
                </div>
              )}

              {groups.length > 0 && (
                <>
                  {orphans.length > 0 && (
                    <div className="text-amber-700">
                      {orphans.length} relay{orphans.length !== 1 ? 's' : ''} on this farm
                      {orphans.length !== 1 ? ' are' : ' is'} watered by a program with no
                      valve node — marked <em>not linked</em> below.
                    </div>
                  )}
                  <div className="flex gap-1">
                    <select
                      className="input flex-1 min-w-0"
                      value={relayChoice}
                      disabled={busy !== null}
                      onChange={(e) => setRelayChoice(e.target.value)}
                    >
                      <option value="">
                        {node.relay_router_mac ? '— move to another relay —' : '— choose a relay —'}
                      </option>
                      {groups.map((g) => (
                        <optgroup
                          key={g.router_mac}
                          label={`${g.title}${g.controller_name ? ` · ${g.controller_name}` : ''} (${nodeStateLabel(g.router_state)})`}
                        >
                          {g.slots.map((s) => {
                            const claim = slotClaim(s, node.id);
                            const taken = s.linked_node_id !== null && s.linked_node_id !== node.id;
                            return (
                              <option
                                key={slotKey(s)}
                                value={slotKey(s)}
                                disabled={taken}
                              >
                                {s.label} (r{s.relay_index})
                                {s.disabled ? ' · out of use' : ''}
                                {claim ? ` · ${claim}` : ''}
                              </option>
                            );
                          })}
                        </optgroup>
                      ))}
                    </select>
                    <button
                      className="px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-40"
                      disabled={!relayChoice || busy !== null}
                      onClick={linkRelay}
                    >
                      {busy === 'relay' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Link'}
                    </button>
                  </div>
                  <div className="text-gray-500">
                    A relay already held by another node cannot be chosen — unlink it there first.
                  </div>
                </>
              )}
            </div>
          )}

          {editable && !takesRelay && !node.relay_router_mac && (
            <div className="mt-1 text-gray-500">
              Relays are linked to valve and pump nodes.
            </div>
          )}
        </section>

        {/* ── Sensor ──────────────────────────────────────────────────── */}
        <section>
          <div className="font-medium text-gray-700 mb-1">Sensor</div>

          {node.sensor_channel_id ? (
            <div className="bg-white border rounded p-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium break-words">
                  {linkedChannel
                    ? `${linkedChannel.device_name || linkedChannel.device_id}${linkedChannel.channel_name ? ` · ${linkedChannel.channel_name}` : ''}`
                    : `Sensor channel #${node.sensor_channel_id}`}
                </div>
                <div className="text-gray-500">
                  {linkedChannel ? `${linkedChannel.type_key} · ch${linkedChannel.channel_index}` : ''}
                  {reading && reading.value !== null && (
                    <> · latest {reading.value}</>
                  )}
                </div>
                {linkedChannel && linkedChannel.type_key !== 'pressure' && (
                  // The live check compares predicted against measured pressure
                  // and ignores everything else, so a moisture channel here
                  // renders on the map and contributes nothing to the analysis.
                  <div className="text-gray-600 mt-0.5">
                    Shown on the map; only a pressure channel feeds the live
                    pressure check.
                  </div>
                )}
              </div>
              {editable && (
                <button
                  className="text-red-600 whitespace-nowrap disabled:opacity-50"
                  disabled={busy !== null}
                  onClick={() => run('sensor', () => irrigationNetworkService.unlinkSensor(node.id))}
                >
                  <Unlink className="w-3 h-3 inline" /> unlink
                </button>
              )}
            </div>
          ) : (
            <div className="text-gray-500">No sensor linked</div>
          )}

          {editable && (
            <div className="mt-1.5 space-y-1">
              {channelsFailed && (
                <div className="text-amber-700">Could not load this farm&rsquo;s sensor channels.</div>
              )}
              {!channelsFailed && channels.length === 0 && (
                <div className="text-gray-500">No sensor channels available.</div>
              )}
              {channels.length > 0 && (
                <>
                  <div className="flex gap-1">
                    <select
                      className="input flex-1 min-w-0"
                      value={sensorChoice}
                      disabled={busy !== null}
                      onChange={(e) => setSensorChoice(e.target.value)}
                    >
                      <option value="">
                        {node.sensor_channel_id ? '— move to another channel —' : '— choose a channel —'}
                      </option>
                      {channels.map((c) => {
                        const held = channelClaims.get(c.id);
                        return (
                          <option key={c.id} value={c.id}>
                            {(c.device_name || c.device_id)}
                            {c.channel_name ? ` · ${c.channel_name}` : ''} (ch{c.channel_index})
                            {` · ${c.type_key}`}
                            {held ? ` · also on ${held}` : ''}
                          </option>
                        );
                      })}
                    </select>
                    <button
                      className="px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-40"
                      disabled={!sensorChoice || busy !== null}
                      onClick={linkSensor}
                    >
                      {busy === 'sensor' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Link'}
                    </button>
                  </div>
                  <div className="text-gray-500">
                    A <strong>pressure</strong> channel is what the live check compares
                    against this node&rsquo;s predicted pressure.
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        {error && <div className="text-red-600">{error}</div>}
      </div>
    </div>
  );
}
